/**
 * useAccessibility - Manages accessibility preferences
 * - High contrast mode
 * - Colorblind mode (deuteranopia: red/green → orange/blue)
 * - Dyslexia font (Lexend)
 * - Text size (normal/large/xl)
 * - Disable animations (beyond prefers-reduced-motion)
 * - Large touch targets
 */
import { useState, useEffect, useCallback } from 'react';

const A11Y_KEY = 'adhd-planner-a11y';

const DEFAULTS = {
  highContrast: false,
  colorblind: false,
  dyslexiaFont: false,
  textSize: 'normal',      // 'normal' | 'large' | 'xl'
  disableAnimations: false,
  largeTouchTargets: false,
};

function loadSettings() {
  try {
    return { ...DEFAULTS, ...JSON.parse(localStorage.getItem(A11Y_KEY) || '{}') };
  } catch {
    return DEFAULTS;
  }
}

export function useAccessibility() {
  const [settings, setSettings] = useState(() => loadSettings());

  // Apply settings to document root as CSS classes and data attributes
  useEffect(() => {
    const root = document.documentElement;

    // High contrast
    root.classList.toggle('a11y-high-contrast', settings.highContrast);
    // Colorblind
    root.classList.toggle('a11y-colorblind', settings.colorblind);
    // Dyslexia font
    root.classList.toggle('a11y-dyslexia', settings.dyslexiaFont);
    // Text size
    root.classList.remove('a11y-text-large', 'a11y-text-xl');
    if (settings.textSize === 'large') root.classList.add('a11y-text-large');
    if (settings.textSize === 'xl') root.classList.add('a11y-text-xl');
    // Disable animations
    root.classList.toggle('a11y-no-motion', settings.disableAnimations);
    // Large touch targets
    root.classList.toggle('a11y-large-targets', settings.largeTouchTargets);

    // Persist
    localStorage.setItem(A11Y_KEY, JSON.stringify(settings));
  }, [settings]);

  const update = useCallback((key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  }, []);

  const reset = useCallback(() => {
    setSettings(DEFAULTS);
  }, []);

  return { settings, update, reset };
}
